import React, {useCallback, useEffect, useMemo, useState} from "react";
import Select, {ClassNamesConfig, StylesConfig} from "react-select";
import {AudioFile} from "../typedefs";
import {useGetAudioFile} from "../hooks/useGetAudioFile";
import {noop} from "../../../lib/misc/noop";
import {reactSelectClassNames, reactSelectStyles} from "../../../lib/react-select/styling";
import {IconButton} from "../../../features/management-v2/import/inputs";
import ModalV2 from "../../../components/portal/ModalV2";
import {useFileSelect} from "../hooks/useFileSelect";
import {usePrettyFileSize} from "../../../lib/files/usePrettyFileSize";
import BasicText from "../../../components/form-v2/basic-text";
import {platformAPI} from "../../../api/axiosConfig";
import {useUploadSirenAudio} from "../hooks/useUploadSirenAudio";
import {Check, FileUploadRounded} from "@mui/icons-material";
import SaveIcon from "@mui/icons-material/Save";
import UploadIcon from "@mui/icons-material/Upload";
import {PaginatedResponseV2} from "../../../types/PaginatedResponse";

type Option = {
    label: string;
    value: string;
};

type AudioFileSelectProps = {
    value?: string;
    onChange?: (audioFile: AudioFile | null) => void;
    clientID: string | null;
};

export function AudioFileSelect({value, onChange = noop, clientID}: AudioFileSelectProps) {
    const [selectedOption, setSelectedOption] = useState<Option | null>(null);
    const [showModal, setShowModal] = useState(false);
    const [search, setSearch] = useState("");
    const [audioFiles, setAudioFiles] = useState<AudioFile[]>([]);
    const [loadingOptions, setLoadingOptions] = useState(false);

    const {data: initialAudioFile, isLoading: loadingInitial} = useGetAudioFile(value);

    const fetchAudioFiles = useCallback(async (q: string) => {
        setLoadingOptions(true);
        try {
            const res = await platformAPI.get<PaginatedResponseV2<AudioFile>>("api/siren/audio-files", {
                params: {page: 1, limit: 10, search: q, ...(clientID ? {clientID} : {})},
            });
            setAudioFiles(res.data.results);
        } finally {
            setLoadingOptions(false);
        }
    }, [clientID]);

    useEffect(() => {
        fetchAudioFiles(search);
    }, [search, fetchAudioFiles]);

    const options: Option[] = useMemo(() => (
        audioFiles.map((f) => ({label: f.name, value: f.id}))
    ), [audioFiles]);

    useEffect(() => {
        if (initialAudioFile && !selectedOption) {
            setSelectedOption({label: initialAudioFile.name, value: initialAudioFile.id});
        }
    }, [initialAudioFile]);

    const handleChange = (option: Option | null) => {
        setSelectedOption(option);
        const selectedFile = audioFiles.find((f) => f.id === option?.value);
        onChange(selectedFile ?? null);
    };

    const {selectedFile, invalidFile, fileInputRef, activateFileSelect, handleFileChange, fileUrl, accept} =
        useFileSelect({allowedTypes: ["mp3", "wav", "m4a"]});

    const {upload, uploading, error: uploadError} = useUploadSirenAudio();
    const fileSize = usePrettyFileSize(selectedFile);
    const [name, setName] = useState("");
    const [saveError, setSaveError] = useState<string | null>(null);

    const canSave = !!(name.trim() && selectedFile && !invalidFile);

    const text = useMemo(() => {
        if (invalidFile) return "Invalid File";
        return selectedFile ? `${selectedFile.name} (${fileSize})` : "Select Audio File";
    }, [selectedFile, invalidFile, fileSize]);

    const icon = useMemo(() => selectedFile ? <Check/> : <FileUploadRounded/>, [selectedFile]);

    const save = useCallback(async () => {
        if (!clientID || !selectedFile) return;
        setSaveError(null);

        const uploadID = await upload(selectedFile, name);
        if (!uploadID) return;

        try {
            await platformAPI.post("api/siren/audio-files", {uploadID, name, clientID});
            await fetchAudioFiles(search);
            setShowModal(false);
        } catch {
            setSaveError("error saving audio file");
        }
    }, [name, selectedFile, clientID, upload, fetchAudioFiles, search]);

    return (
        <div className="flex flex-col gap-4">
            <ModalV2 open={showModal} setOpen={setShowModal}>
                <h3 className="text-lg font-bold">Upload Audio File</h3>
                <BasicText label="Name" value={name} onChange={setName} required/>
                <input ref={fileInputRef} type="file" accept={accept} onChange={handleFileChange} className="hidden"/>
                <IconButton onClick={activateFileSelect} text={text} icon={icon}/>
                {fileUrl && <audio src={fileUrl} controls className="w-full"/>}
                {canSave && <IconButton onClick={save} text="Upload" icon={<SaveIcon/>} loading={uploading}/>}
                {uploadError && <p className="text-center text-red">{uploadError}</p>}
                {saveError && <p className="text-center text-red">{saveError}</p>}
            </ModalV2>
            <Select
                classNames={reactSelectClassNames as ClassNamesConfig<Option>}
                styles={reactSelectStyles as StylesConfig<Option, false>}
                isLoading={loadingInitial || loadingOptions}
                value={selectedOption}
                onChange={handleChange}
                options={options}
                onInputChange={(input) => setSearch(input)}
                placeholder="Select audio file..."
                isClearable
                filterOption={null}
            />
            <IconButton text="Upload New" onClick={() => setShowModal(true)} icon={<UploadIcon/>}/>
        </div>
    );
}
